import { createGlobalState, tryOnScopeDispose } from '@vueuse/core';
import EventEmitter from 'eventemitter3';
import { computed, nextTick, ref } from 'vue';

import type { MenuForm } from '#public_ts/pages/main-menu/globals';

export const MainMenuEventListener = Symbol('main-menu-events');
export type MainMenuEvents = {
    'sidenav-mounted': (mount: boolean) => void;
    'sidenav-compact': (compact: boolean) => void;
    'fattapp2-activate': (activate: boolean) => void;
    'menu-panes': (panes: Map<string, MenuForm>) => void;
};

type EventMapLookup = {
    [MainMenuEventListener]: MainMenuEvents;
};

const _useGlobalEvents = createGlobalState(() => {
    const emitters = new Map<symbol, EventEmitter<string | object>>();

    function getEventEmitter<T extends keyof EventMapLookup>(type: T): EventEmitter<EventMapLookup[T]> {
        const emitter = emitters.get(type);
        if(emitter) return emitter as EventEmitter<EventMapLookup[T]>;
        const newEmitter = new EventEmitter<EventMapLookup[T]>();
        emitters.set(type, newEmitter as EventEmitter<string | object>);
        return newEmitter;
    }
    return getEventEmitter;
});

export function useGlobalEvents<T extends keyof EventMapLookup>(type: T) {
    return _useGlobalEvents()(type);
}

export function listenGlobalEvent<
    T extends keyof EventMapLookup,
    E extends keyof EventMapLookup[T],
>(type: T, event: E, listener: EventMapLookup[T][E]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const eventEmitter = useGlobalEvents(type) as any;
    eventEmitter.on(event, listener);
    tryOnScopeDispose(() => {
        eventEmitter.off(event, listener);
    });
}

// main menu is separate vue application
// we cannot share refs from one app to another, so we use events to sync state
export const useMainMenuState = createGlobalState(() => {
    let mounted = false;
    let compact = false;
    let fattapp2Active = false;
    let menuPanes = new Map<string, MenuForm>();
    const onMounted = (_mounted: boolean) => { mounted = _mounted; };
    const onCompact = (_compact: boolean) => { compact = _compact; };
    const onFattapp2Active = (_activate: boolean) => { fattapp2Active = _activate; };
    const onMenuPanes = (_panes: Map<string, MenuForm>) => { menuPanes = _panes; };
    listenGlobalEvent(MainMenuEventListener, 'sidenav-mounted', onMounted);
    listenGlobalEvent(MainMenuEventListener, 'sidenav-compact', onCompact);
    listenGlobalEvent(MainMenuEventListener, 'fattapp2-activate', onFattapp2Active);
    listenGlobalEvent(MainMenuEventListener, 'menu-panes', onMenuPanes);
    const mainMenuEvents = useGlobalEvents(MainMenuEventListener);

    return {
        isMounted() { return mounted; },
        isCompact() { return compact; },
        isFattapp2Active() { return fattapp2Active; },
        setMounted(_mounted: boolean) { mainMenuEvents.emit('sidenav-mounted', _mounted); },
        setCompact(_compact: boolean) { mainMenuEvents.emit('sidenav-compact', _compact); },
        setFattapp2Active(_activate: boolean) { mainMenuEvents.emit('fattapp2-activate', _activate); },
        menuPanes() { return menuPanes; },
        setMenuPanes(_menuPanes: Map<string, MenuForm>) {
            mainMenuEvents.emit(
                'menu-panes',
                new Map<string, MenuForm>([..._menuPanes.entries()].map(([key, value]) => [key, { ...value }])),
            );
        },
    };
});

export function useMainMenuStateRef() {
    const {
        isMounted: _isMounted,
        isCompact: _isCompact,
        isFattapp2Active: _isFattapp2Active,
        menuPanes: _menuPanes,
    } = useMainMenuState();
    const isMounted = ref(_isMounted());
    const isCompact = ref(_isCompact());
    const isMountedAndCompact = computed(() => isMounted.value && isCompact.value);
    const isFattapp2Active = ref(_isFattapp2Active());
    const menuPanes = ref(_menuPanes());

    listenGlobalEvent(MainMenuEventListener, 'sidenav-mounted', (mounted) => {
        if(mounted) nextTick(() => { isMounted.value = mounted; });
        else isMounted.value = mounted;
    });

    listenGlobalEvent(MainMenuEventListener, 'sidenav-compact', (compact) => {
        isCompact.value = compact;
    });

    listenGlobalEvent(MainMenuEventListener, 'fattapp2-activate', (activate) => {
        isFattapp2Active.value = activate;
    });

    listenGlobalEvent(MainMenuEventListener, 'menu-panes', (panes) => {
        menuPanes.value = panes;
    });

    return {
        isMounted,
        isCompact,
        isMountedAndCompact,
        isFattapp2Active,
        menuPanes,
    };
}
